import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseApiError } from '../utils/errors';
import Logo from '../components/Logo';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  // Load Google reCAPTCHA only when a site key is configured
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (document.querySelector('script[data-recaptcha]')) return;
    const s = document.createElement('script');
    s.src = 'https://www.google.com/recaptcha/api.js';
    s.async = true; s.defer = true; s.setAttribute('data-recaptcha', '1');
    document.body.appendChild(s);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    let captchaToken;
    if (RECAPTCHA_SITE_KEY) {
      captchaToken = window.grecaptcha?.getResponse();
      if (!captchaToken) { setError('Please complete the captcha.'); return; }
    }
    setLoading(true);
    try {
      const u = await login(form.username, form.password, captchaToken);
      if (u.role === 'super_admin') navigate('/admin/dashboard');
      else if (u.role === 'hr') navigate('/hr/dashboard');
      else navigate('/employee/dashboard');
    } catch (err) {
      setError(parseApiError(err, 'Login failed. Check your credentials.'));
      if (RECAPTCHA_SITE_KEY) window.grecaptcha?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <Logo className="login-logo-img" fallbackClassName="login-logo" />
          <h1>TaskTool</h1>
          <p>Task &amp; Work Report Management System</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="Enter your username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Enter your password"
            />
          </div>
          {RECAPTCHA_SITE_KEY && (
            <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} style={{ margin: '4px 0' }} />
          )}
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="login-footer">Internal system — authorized personnel only</p>
      </div>
    </div>
  );
}
